/**
 * Per-patch contribution diff.
 *
 * Given a stored patch, the post-regen `currentGen` content of each file, and
 * the working-tree (post-resolution) content, compute a unified diff that
 * captures ONLY this patch's contribution — scoped to the regions identified
 * by the patch's stored hunks via context-line anchoring.
 *
 * Why this exists: `git diff currentGen -- patch.files` returns the full
 * cumulative diff for those files. When two patches share a file, the
 * cumulative diff includes BOTH patches' customizations — which means each
 * patch ends up with the same `patch_content` and `content_hash`. The
 * dedup-by-hash logic downstream then collapses them into one and silently
 * loses provenance.
 */

import { mkdtemp, writeFile, rm } from "node:fs/promises";
import { tmpdir } from "node:os";
import { join } from "node:path";
import { spawn } from "node:child_process";
import { parseHunks, locateHunksInOurs, type DiffHunk, type LocatedHunk } from "./HybridReconstruction.js";

/**
 * Trim a hunk's body to exactly the line counts declared in its `@@` header.
 *
 * `parseHunks` keeps appending lines to a hunk's body until it hits the next
 * `@@` header or end of input. When the patch text has trailing blank lines
 * (e.g. from a trailing newline in the multi-file diff), those blanks get
 * conflated with context lines (per the helper's "bare empty = context"
 * heuristic), inflating the parsed body past `oldCount`/`newCount`. That
 * inflation causes `locateHunksInOurs` to overshoot when computing
 * `oursSpan` because trailing context anchoring lands on lines beyond the
 * hunk's actual region.
 */
function normalizeHunkBody(hunk: DiffHunk): DiffHunk {
    let contextC = 0;
    let removeC = 0;
    let addC = 0;
    const trimmed: typeof hunk.lines = [];
    for (const line of hunk.lines) {
        const oldUsed = contextC + removeC;
        const newUsed = contextC + addC;
        if (oldUsed >= hunk.oldCount && newUsed >= hunk.newCount) break;
        trimmed.push(line);
        if (line.type === "context") contextC++;
        else if (line.type === "remove") removeC++;
        else if (line.type === "add") addC++;
    }
    return { ...hunk, lines: trimmed };
}

export interface PerPatchDiffResult {
    /** Concatenated unified diff (one `diff --git` block per file). Empty string if patch contributes nothing. */
    diff: string;
    /** Files where the patch's hunks could not be anchored against either currentGen or working tree. Caller should fall back. */
    unlocatableFiles: string[];
}

/**
 * Extract the per-file unified diff for `filePath` from a multi-file unified diff.
 * Mirrors `ReplayApplicator.extractFileDiff` — kept here to avoid cross-module
 * private-method exposure.
 */
export function extractFileDiffForFile(patchContent: string, filePath: string): string | null {
    const lines = patchContent.split("\n");
    const out: string[] = [];
    let inTarget = false;
    for (const line of lines) {
        if (line.startsWith("diff --git")) {
            if (inTarget) break;
            if (isDiffLineForFile(line, filePath)) {
                inTarget = true;
                out.push(line);
            }
            continue;
        }
        if (inTarget) out.push(line);
    }
    return out.length > 0 ? out.join("\n") + "\n" : null;
}

function isDiffLineForFile(line: string, filePath: string): boolean {
    // Use anchored regex (matches `ReplayApplicator.ts` pattern) to avoid
    // substring false positives — e.g. `src/user.ts` matching `src/user.tsx`,
    // or `lib/foo.ts` matching `lib/foo.ts-backup`. Match on the b-path
    // (post-image) since it's the canonical destination, including for
    // renames (`diff --git a/old b/new`).
    const match = line.match(/^diff --git a\/(.+) b\/(.+)$/);
    return match !== null && (match[2] === filePath || match[1] === filePath);
}

/**
 * Compute the per-patch contribution diff.
 *
 * The caller owns content reads via `getCurrentGenContent` / `getWorkingTreeContent`
 * so that the helper is reusable from `resolve.ts` (reads from disk) and
 * `rebasePatches` (reads from git tree + disk).
 *
 * Algorithm:
 *  1. For each file in `patch.files`:
 *     a. Parse the patch's hunks for this file.
 *     b. Anchor the hunks in `currentGen` content via `locateHunksInOurs`.
 *     c. Anchor the hunks in `workingTree` content via `locateHunksInOurs`.
 *     d. Overlay each anchored region from working tree onto currentGen,
 *        producing a "currentGen + just this patch's contribution" string.
 *     e. Compute a unified diff between currentGen and that overlay via
 *        `git diff --no-index`. This is the per-patch diff for the file.
 *  2. Concatenate per-file diffs.
 *
 * Falls back to `unlocatableFiles` for files where steps b/c fail (rename,
 * heavy generator restructure, etc.) — caller's responsibility to handle.
 */
export async function computePerPatchDiff(
    patch: { patch_content: string; files: string[] },
    getCurrentGenContent: (file: string) => Promise<string | null>,
    getWorkingTreeContent: (file: string) => Promise<string | null>
): Promise<PerPatchDiffResult> {
    const fragments: string[] = [];
    const unlocatableFiles: string[] = [];

    for (const file of patch.files) {
        const fileDiff = extractFileDiffForFile(patch.patch_content, file);
        if (fileDiff == null) {
            // patch.files lists this path, but patch_content doesn't reference it.
            // This commonly happens after a generator rename (resolvedFiles pre-pass
            // updated patch.files to the NEW path, but patch_content still uses the
            // OLD path). Treat as unlocatable so the caller falls back to the legacy
            // cumulative-file diff for this file.
            unlocatableFiles.push(file);
            continue;
        }

        const hunksRaw = parseHunks(fileDiff).map(normalizeHunkBody);
        // M1: drop hunks whose body is empty after trimming or whose body
        // line counts don't match the `@@` header. Either is a sign of a
        // malformed/truncated diff; passing them downstream into
        // `locateHunksInOurs` would yield garbage offsets and silently
        // corrupt the overlay.
        const hunks = hunksRaw.filter((h) => {
            if (h.lines.length === 0) return false;
            let ctx = 0, rm = 0, add = 0;
            for (const ln of h.lines) {
                if (ln.type === "context") ctx++;
                else if (ln.type === "remove") rm++;
                else if (ln.type === "add") add++;
            }
            return ctx + rm === h.oldCount && ctx + add === h.newCount;
        });
        if (hunks.length === 0) {
            // If parseHunks returned hunks but none survived the body check,
            // signal unlocatable so the caller falls back to cumulative diff.
            if (hunksRaw.length > 0) unlocatableFiles.push(file);
            continue;
        }

        const currentGen = await getCurrentGenContent(file);
        const workingTree = await getWorkingTreeContent(file);

        // Determine whether the patch is a pure new-file patch (no remove lines
        // anywhere). True new-file patches have all hunks with oldCount === 0.
        const isPureNewFile = hunks.every((h) => h.oldCount === 0);
        const isPureDelete = hunks.every((h) => h.newCount === 0);

        // New-file patch: file didn't exist in currentGen. Only valid if patch is
        // genuinely a new-file patch — otherwise we may be in the ghost case
        // (currentGen tree unreachable for a file the patch knows existed).
        if (currentGen == null) {
            if (!isPureNewFile) {
                unlocatableFiles.push(file);
                continue;
            }
            if (workingTree == null) continue;
            fragments.push(synthesizeNewFileDiff(file, workingTree));
            continue;
        }

        // Pure-new-file patch where currentGen ALREADY has the file: the
        // user-owned bundling case (a previous generation commit bundled
        // the customer's file into the gen tree via `git add -A`). Compare full
        // contents directly — anchoring an empty `oldSide` would over-grab.
        // If contents match → no contribution since last cycle. If they
        // differ → customer modified, emit a full-file diff.
        if (isPureNewFile) {
            if (workingTree == null) {
                // File deleted in working tree → emit deletion diff.
                fragments.push(synthesizeDeleteFileDiff(file, currentGen));
                continue;
            }
            if (workingTree === currentGen) {
                // No change since bundling — no contribution.
                continue;
            }
            const fullDiff = await unifiedDiff(currentGen, workingTree, file);
            if (fullDiff && fullDiff.trim()) fragments.push(fullDiff);
            continue;
        }

        // Deleted in working tree: file existed in currentGen.
        if (workingTree == null) {
            if (!isPureDelete) {
                unlocatableFiles.push(file);
                continue;
            }
            fragments.push(synthesizeDeleteFileDiff(file, currentGen));
            continue;
        }

        const currentGenLines = splitLines(currentGen);
        const workingTreeLines = splitLines(workingTree);

        const locInCurrentGenRaw = locateHunksInOurs(hunks, currentGenLines);
        const locInWorkingTreeRaw = locateHunksInOurs(hunks, workingTreeLines);

        if (locInCurrentGenRaw == null || locInWorkingTreeRaw == null) {
            unlocatableFiles.push(file);
            continue;
        }

        // HybridReconstruction.computeOursSpan returns the context
        // count when trailing context is empty, which doesn't account for
        // remove/add lines. Re-derive span by content matching with a
        // domain hint: currentGen is expected to look like the patch's "old"
        // side (pre-patch); workingTree is expected to look like the "new"
        // side (post-patch). The hint disambiguates pure-insertion hunks
        // where oldSide is a prefix of newSide and both fit by coincidence.
        //
        // If ANY hunk fails to resolve (neither side fits OURS at the
        // anchor), mark the file unlocatable so the caller falls back to a
        // cumulative diff. Per-patch isolation is no longer honest here:
        // the previous fallthrough returned a raw span that silently
        // truncated `+` lines on overlay.
        const locInCurrentGen: LocatedHunk[] = [];
        const locInWorkingTree: LocatedHunk[] = [];
        let spanResolutionFailed = false;
        for (let i = 0; i < locInCurrentGenRaw.length; i++) {
            const cg = resolveSpan(locInCurrentGenRaw[i]!, currentGenLines, "old");
            const wt = resolveSpan(locInWorkingTreeRaw[i]!, workingTreeLines, "new");
            if (cg === null || wt === null) {
                spanResolutionFailed = true;
                break;
            }
            locInCurrentGen.push(cg);
            locInWorkingTree.push(wt);
        }
        if (spanResolutionFailed) {
            unlocatableFiles.push(file);
            continue;
        }

        // Overlay each region from working tree onto currentGen.
        const overlay = overlayRegions(
            currentGenLines,
            locInCurrentGen,
            workingTreeLines,
            locInWorkingTree
        );

        // M2: overlay returned null → invariant violation (mismatched
        // anchored hunk counts). Signal unlocatable so caller falls back
        // to cumulative; do NOT silently treat as "no contribution".
        if (overlay === null) {
            unlocatableFiles.push(file);
            continue;
        }

        if (overlay === currentGen) {
            // Patch contributed nothing in this file — customer reverted or absorbed.
            continue;
        }

        const fileUnifiedDiff = await unifiedDiff(currentGen, overlay, file);
        if (fileUnifiedDiff && fileUnifiedDiff.trim()) {
            fragments.push(fileUnifiedDiff);
        }
    }

    return {
        diff: fragments.join(""),
        unlocatableFiles
    };
}

/**
 * Combine a per-patch diff with a cumulative-diff fallback for any files
 * whose hunks couldn't be anchored. This is the helper used by both
 * `commands/resolve.ts` and `ReplayService.preGenerationRebase` to ensure
 * unlocatable files don't silently drop the patch when the locatable
 * subset's per-patch diff is empty.
 *
 * Algorithm:
 *   1. Run per-patch diff on `patch.files`.
 *   2. If `unlocatableFiles` is non-empty, run cumulative diff scoped to
 *      ONLY those unlocatable files via `runCumulativeDiff(files)`.
 *   3. Concatenate the two. Return null if the cumulative invocation fails.
 *
 * Returns:
 *   - `{ diff, hadFallback }` where `diff` may be empty (true patch revert)
 *     and `hadFallback` indicates whether the cumulative subset was used.
 *   - `null` if the cumulative invocation explicitly failed (callers should
 *     skip the patch — same semantics as a missing diff).
 */
export async function computePerPatchDiffWithFallback(
    patch: { patch_content: string; files: string[] },
    getCurrentGenContent: (file: string) => Promise<string | null>,
    getWorkingTreeContent: (file: string) => Promise<string | null>,
    runCumulativeDiff: (files: string[]) => Promise<string | null>
): Promise<{ diff: string; hadFallback: boolean; fallbackFiles: string[] } | null> {
    const ppDiff = await computePerPatchDiff(
        patch,
        getCurrentGenContent,
        getWorkingTreeContent
    );

    if (ppDiff.unlocatableFiles.length === 0) {
        return { diff: ppDiff.diff, hadFallback: false, fallbackFiles: [] };
    }

    // Run cumulative diff scoped to ONLY the unlocatable files. Merging
    // them with the per-patch diff for locatable files preserves the
    // surgical scoping for files we COULD anchor, while still capturing
    // contributions for files we couldn't.
    const cumulative = await runCumulativeDiff(ppDiff.unlocatableFiles);
    if (cumulative === null) {
        return null;
    }

    const merged = ppDiff.diff + (cumulative.trim() ? cumulative : "");
    return { diff: merged, hadFallback: true, fallbackFiles: ppDiff.unlocatableFiles };
}

function splitLines(content: string): string[] {
    // Match parseHunks's line semantics: split on "\n", preserving trailing-newline behavior.
    return content.split("\n");
}

/**
 * Determine the correct `oursSpan` for an anchored hunk by content-matching.
 *
 * `prefer` tells us which side of the patch we EXPECT OURS to look like:
 *   - `"old"`  → OURS is patch's BASE (e.g. currentGen, pre-patch state).
 *                Default to `oldCount` when both sides fit.
 *   - `"new"`  → OURS is patch's THEIRS (e.g. workingTree post-apply).
 *                Default to `newCount` when both sides fit.
 *
 * For pure-insertion hunks (no remove lines), `oldSide` is a strict prefix
 * of `newSide`. Both sides will fit whenever oldSide does AND the next OURS
 * lines coincide with the patch's added lines — common for generic adds
 * like `}` or empty lines. Without the `prefer` hint, picking the longer
 * (or shorter) match unconditionally over-includes (or under-includes)
 * sibling-patch content. With `prefer`, we use the caller's domain
 * knowledge to pick the right span.
 *
 * Asymmetric null-return:
 *   - `prefer === "new"` (workingTree): if neither side fits, the patch's
 *     idea of the customer's content is stale (customer restructured the
 *     region past either shape the hunk depicts). Return `null` so the
 *     caller routes to the cumulative-diff fallback — previously this
 *     fell through to a stale `oursSpan`, producing an overlay that
 *     dropped `+` lines while keeping `-` lines (silent customer-
 *     customization loss; smallest-ai).
 *   - `prefer === "old"` (currentGen): keep the best-effort fallthrough.
 *     The generator commonly restructures the region during a regen, in
 *     which case currentGen matches neither side, but the
 *     trailing-context-anchored position from `HybridReconstruction` is
 *     still the right place to splice in workingTree's content. Returning
 *     null here would over-fire for every conflicting regen and collapse
 *     disjoint-region patches into a single cumulative diff.
 */
function resolveSpan(
    loc: LocatedHunk,
    oursLines: string[],
    prefer: "old" | "new"
): LocatedHunk | null {
    const { hunk, oursOffset } = loc;

    // Build the expected "old" content sequence from the hunk: context + remove lines, in order.
    const oldSide: string[] = [];
    const newSide: string[] = [];
    for (const line of hunk.lines) {
        if (line.type === "context") {
            oldSide.push(line.content);
            newSide.push(line.content);
        } else if (line.type === "remove") {
            oldSide.push(line.content);
        } else if (line.type === "add") {
            newSide.push(line.content);
        }
    }

    const oldFits = sequenceMatches(oldSide, oursLines, oursOffset);
    const newFits = sequenceMatches(newSide, oursLines, oursOffset);

    if (prefer === "old") {
        if (oldFits) return { ...loc, oursSpan: hunk.oldCount };
        if (newFits) return { ...loc, oursSpan: hunk.newCount };
        // currentGen diverged from both sides (generator restructured this
        // region in the regen). Fall through to the raw computeOursSpan
        // from HybridReconstruction — trailing-context anchoring still
        // gives a positionally-correct splice point.
        return loc;
    }

    // prefer === "new"
    if (newFits) return { ...loc, oursSpan: hunk.newCount };
    if (oldFits) return { ...loc, oursSpan: hunk.oldCount };
    return null;
}

function sequenceMatches(needle: string[], haystack: string[], offset: number): boolean {
    if (offset + needle.length > haystack.length) return false;
    for (let i = 0; i < needle.length; i++) {
        if (haystack[offset + i] !== needle[i]) return false;
    }
    return true;
}

/**
 * Reconstruct currentGen with each anchored region replaced by the
 * corresponding region from working tree. Returns the joined string, or
 * `null` to signal an irrecoverable inconsistency (caller should treat as
 * unlocatable rather than silently produce a corrupt overlay).
 */
function overlayRegions(
    currentGenLines: string[],
    locInCurrentGen: LocatedHunk[],
    workingTreeLines: string[],
    locInWorkingTree: LocatedHunk[]
): string | null {
    // Same hunks parsed against both, lengths must match. If they don't,
    // something invariant-violating happened — signal failure rather than
    // returning currentGen (which the caller would interpret as "patch
    // contributed nothing" and silently drop the patch).
    if (locInCurrentGen.length !== locInWorkingTree.length) {
        return null;
    }
    const out: string[] = [];
    let cursor = 0;
    for (let i = 0; i < locInCurrentGen.length; i++) {
        const cg = locInCurrentGen[i]!;
        const wt = locInWorkingTree[i]!;
        // Gap before this hunk: keep currentGen content
        if (cg.oursOffset > cursor) {
            out.push(...currentGenLines.slice(cursor, cg.oursOffset));
        }
        // Replace currentGen's region with working tree's region for this hunk
        out.push(...workingTreeLines.slice(wt.oursOffset, wt.oursOffset + wt.oursSpan));
        cursor = cg.oursOffset + cg.oursSpan;
    }
    // Trailing gap: keep currentGen content
    if (cursor < currentGenLines.length) {
        out.push(...currentGenLines.slice(cursor));
    }
    return out.join("\n");
}

/**
 * Compute a unified diff between two strings using `git diff --no-index`.
 * Headers are rewritten to `a/<file>` / `b/<file>` for compatibility with
 * `git apply` and the rest of the codebase's patch handling.
 */
export async function unifiedDiff(beforeContent: string, afterContent: string, filePath: string): Promise<string> {
    if (beforeContent === afterContent) return "";

    const tmp = await mkdtemp(join(tmpdir(), "fern-replay-pp-"));
    try {
        const beforePath = join(tmp, "before");
        const afterPath = join(tmp, "after");
        await writeFile(beforePath, beforeContent, "utf-8");
        await writeFile(afterPath, afterContent, "utf-8");

        const raw = await runGitDiffNoIndex(beforePath, afterPath);
        if (!raw.trim()) return "";

        return rewriteDiffHeaders(raw, filePath);
    } finally {
        await rm(tmp, { recursive: true, force: true });
    }
}

function runGitDiffNoIndex(beforePath: string, afterPath: string): Promise<string> {
    return new Promise((resolve, reject) => {
        const proc = spawn("git", ["diff", "--no-index", "--", beforePath, afterPath]);
        let stdout = "";
        let stderr = "";
        proc.stdout.on("data", (d: Buffer) => (stdout += d.toString()));
        proc.stderr.on("data", (d: Buffer) => (stderr += d.toString()));
        proc.on("close", (code) => {
            // git diff --no-index returns 0 (no diff) or 1 (diff present) on success;
            // anything else is a real error.
            if (code === 0 || code === 1) resolve(stdout);
            else reject(new Error(`git diff --no-index failed (code ${code}): ${stderr}`));
        });
    });
}

/**
 * Rewrite the `diff --git`, `---`, and `+++` headers from temp paths to
 * `a/<file>` and `b/<file>` so the diff applies cleanly via `git apply`.
 */
function rewriteDiffHeaders(raw: string, filePath: string): string {
    const lines = raw.split("\n");
    const out: string[] = [];
    let seenFirstHeader = false;
    for (const line of lines) {
        if (line.startsWith("diff --git ")) {
            out.push(`diff --git a/${filePath} b/${filePath}`);
            seenFirstHeader = true;
            continue;
        }
        if (!seenFirstHeader) {
            // Skip preamble before first diff header
            continue;
        }
        if (line.startsWith("--- ")) {
            out.push(`--- a/${filePath}`);
            continue;
        }
        if (line.startsWith("+++ ")) {
            out.push(`+++ b/${filePath}`);
            continue;
        }
        out.push(line);
    }
    return out.join("\n");
}

export function synthesizeNewFileDiff(file: string, content: string): string {
    const lines = content.split("\n");
    // Drop trailing empty element if file ends with newline (matches git's diff format).
    const hasTrailingNewline = content.endsWith("\n");
    const bodyLines = hasTrailingNewline ? lines.slice(0, -1) : lines;
    const header = [
        `diff --git a/${file} b/${file}`,
        "new file mode 100644",
        "--- /dev/null",
        `+++ b/${file}`,
        `@@ -0,0 +1,${bodyLines.length} @@`,
    ].join("\n");
    const body = bodyLines.map((l) => `+${l}`).join("\n");
    const trailer = hasTrailingNewline ? "" : "\n\\ No newline at end of file";
    return header + "\n" + body + trailer + "\n";
}

export function synthesizeDeleteFileDiff(file: string, content: string): string {
    const lines = content.split("\n");
    const hasTrailingNewline = content.endsWith("\n");
    const bodyLines = hasTrailingNewline ? lines.slice(0, -1) : lines;
    const header = [
        `diff --git a/${file} b/${file}`,
        "deleted file mode 100644",
        `--- a/${file}`,
        "+++ /dev/null",
        `@@ -1,${bodyLines.length} +0,0 @@`,
    ].join("\n");
    const body = bodyLines.map((l) => `-${l}`).join("\n");
    const trailer = hasTrailingNewline ? "" : "\n\\ No newline at end of file";
    return header + "\n" + body + trailer + "\n";
}
